import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Search, Building2 } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { errorText } from '../../lib/errors';
import {
  PageHeader, Button, DataTable, StatusBadge, Avatar, EmptyState, Input, Select, Switch, Modal, Field,
} from '../../ui';

const STATUSES = ['active', 'trial', 'past_due', 'suspended'];
const PLANS = ['trial', 'starter', 'pro', 'enterprise'];
const CYCLES = ['monthly', 'quarterly', 'yearly'];
const CURRENCIES = ['UAH', 'USD', 'EUR'];

const hasProblem = (c) => c.billing_state === 'overdue' || (c.status === 'active' && ['error', 'degraded', 'unconfigured'].includes(c.pos_health));

export default function CompaniesPage() {
  const { t, fmt } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const status = params.get('status') || '';
  const problemsOnly = params.get('problems') === '1';

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/platform/overview');
      setCompanies(res.data.companies || []);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return companies.filter((c) =>
      (!q || `${c.name} ${c.company_code ?? ''}`.toLowerCase().includes(q)) &&
      (!status || c.status === status) &&
      (!problemsOnly || hasProblem(c))
    );
  }, [companies, query, status, problemsOnly]);

  const columns = [
    {
      key: 'name',
      header: t('companies.company'),
      sortable: true,
      render: (c) => (
        <div className="flex items-center gap-3">
          <Avatar name={c.name} size="sm" className="rounded-md" />
          <div>
            <p className="font-medium text-zinc-900 dark:text-zinc-100">{c.name}</p>
            <p className="font-mono text-[11px] text-zinc-400">{c.company_code}</p>
          </div>
        </div>
      ),
    },
    { key: 'status', header: t('common.status'), sortable: true, render: (c) => <StatusBadge kind="company" value={c.status} /> },
    {
      key: 'monthly_price',
      header: t('companies.plan'),
      sortable: true,
      hideOnMobile: true,
      render: (c) => (
        <div>
          <p className="text-zinc-800 dark:text-zinc-200">{t(`plans.${c.plan}`)}</p>
          <p className="tabular text-xs text-zinc-500">{c.monthly_price > 0 ? `${fmt.money(c.monthly_price, c.currency)}${t('perMonth')}` : '—'}</p>
        </div>
      ),
    },
    { key: 'employees', header: t('companies.employees'), sortable: true, align: 'right', render: (c) => <span className="tabular">{fmt.number(c.employees)}</span> },
    { key: 'balance', header: t('companies.balance'), sortable: true, align: 'right', hideOnMobile: true, render: (c) => <span className="tabular">{fmt.money(c.balance)}</span> },
    { key: 'tx30_sum', header: t('companies.spend30'), sortable: true, align: 'right', hideOnMobile: true, render: (c) => <span className="tabular">{fmt.money(c.tx30_sum)}</span> },
    { key: 'pos_health', header: t('companies.pos'), sortable: true, render: (c) => <StatusBadge kind="pos" value={c.pos_health} /> },
    {
      key: 'billing_state',
      header: t('companies.billing'),
      sortable: true,
      sortValue: (c) => c.billing_next_date || '9999',
      hideOnMobile: true,
      render: (c) => (
        <div className="flex flex-col items-start gap-0.5">
          <StatusBadge kind="billing" value={c.billing_state} />
          {c.billing_next_date && <span className="tabular text-[11px] text-zinc-500">{fmt.date(c.billing_next_date)}</span>}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('companies.title')}
        description={t('companies.subtitle')}
        actions={<Button icon={Plus} onClick={() => setCreateOpen(true)}>{t('companies.new')}</Button>}
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="sm:w-80">
          <Input icon={Search} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('companies.searchPlaceholder')} aria-label={t('common.search')} />
        </div>
        <Select value={status} onChange={(e) => setFilter('status', e.target.value)} className="sm:w-48" aria-label={t('companies.filterStatus')}>
          <option value="">{t('common.all')}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`status.company.${s}`)}</option>)}
        </Select>
        <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
          <Switch checked={problemsOnly} onChange={(v) => setFilter('problems', v ? '1' : '')} label={t('companies.onlyProblems')} />
          {t('companies.onlyProblems')}
        </label>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        onRowClick={(c) => navigate(`/owner/companies/${c.id}`)}
        empty={<EmptyState icon={Building2} title={t('companies.empty')} text={t('companies.emptyHint')} />}
      />

      <CreateCompanyModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(tenant) => navigate(`/owner/companies/${tenant.id}?tab=pos`)} />
    </>
  );
}

const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

function CreateCompanyModal({ open, onClose, onCreated }) {
  const { t } = useI18n();
  const empty = {
    name: '', slug: '', company_code: '',
    adminUsername: '', adminEmail: '', adminPassword: '',
    plan: 'pro', plan_price: '', billing_cycle: 'monthly', currency: 'UAH',
  };
  const [form, setForm] = useState(empty);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setForm(empty); setSlugTouched(false); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug.trim(),
        company_code: form.company_code || undefined,
        plan_price: form.plan_price === '' ? 0 : Number(form.plan_price),
        adminUsername: form.adminUsername || undefined,
        adminEmail: form.adminEmail || undefined,
        adminPassword: form.adminPassword || undefined,
      };
      const res = await api.post('/admin/tenants', payload);
      toast.success(t('companies.created'));
      onClose();
      onCreated(res.data.data.tenant);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const section = 'text-xs font-semibold uppercase tracking-wide text-zinc-400';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('companies.createTitle')}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="create-company-form" loading={saving}>{t('common.create')}</Button>
        </>
      }
    >
      <form id="create-company-form" onSubmit={submit} className="space-y-6">
        <div className="space-y-4">
          <p className={section}>{t('companies.createStepCompany')}</p>
          <Field label={t('companies.name')} required>
            <Input
              value={form.name}
              onChange={(e) => set({ name: e.target.value, ...(slugTouched ? {} : { slug: slugify(e.target.value) }) })}
              required
              autoFocus
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('companies.slug')} hint={t('companies.slugHint')} required>
              <Input
                value={form.slug}
                onChange={(e) => { setSlugTouched(true); set({ slug: e.target.value }); }}
                pattern="[A-Za-z0-9_\-]{2,50}"
                required
                className="font-mono"
              />
            </Field>
            <Field label={t('companies.companyCode')} hint={t('companies.companyCodeHint')}>
              <Input
                value={form.company_code}
                onChange={(e) => set({ company_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                placeholder={form.slug.toUpperCase().replace(/[^A-Z0-9]/g, '')}
                maxLength={20}
                className="font-mono"
              />
            </Field>
          </div>
        </div>

        <div className="space-y-4">
          <p className={section}>{t('companies.createStepAdmin')}</p>
          <p className="-mt-2 text-xs text-zinc-500 dark:text-zinc-400">{t('companies.adminHint')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('common.username')}>
              <Input value={form.adminUsername} onChange={(e) => set({ adminUsername: e.target.value })} autoComplete="off" />
            </Field>
            <Field label={t('common.email')}>
              <Input type="email" value={form.adminEmail} onChange={(e) => set({ adminEmail: e.target.value })} autoComplete="off" />
            </Field>
          </div>
          <Field label={t('common.password')} hint={t('team.passwordHint')}>
            <Input type="password" value={form.adminPassword} onChange={(e) => set({ adminPassword: e.target.value })} minLength={8} autoComplete="new-password" />
          </Field>
        </div>

        <div className="space-y-4">
          <p className={section}>{t('companies.createStepPlan')}</p>
          <div className="grid gap-4 sm:grid-cols-4">
            <Field label={t('companies.plan')}>
              <Select value={form.plan} onChange={(e) => set({ plan: e.target.value })}>
                {PLANS.map((p) => <option key={p} value={p}>{t(`plans.${p}`)}</option>)}
              </Select>
            </Field>
            <Field label={t('companies.price')}>
              <Input type="number" min="0" step="1" value={form.plan_price} onChange={(e) => set({ plan_price: e.target.value })} className="tabular" />
            </Field>
            <Field label={t('companies.cycle')}>
              <Select value={form.billing_cycle} onChange={(e) => set({ billing_cycle: e.target.value })}>
                {CYCLES.map((c) => <option key={c} value={c}>{t(`cycles.${c}`)}</option>)}
              </Select>
            </Field>
            <Field label={t('companies.currency')}>
              <Select value={form.currency} onChange={(e) => set({ currency: e.target.value })}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
          </div>
        </div>
      </form>
    </Modal>
  );
}
